import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { PostsService } from '../../../shared/services/posts/posts.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { catchError, filter, from, map, mergeMap, of, switchMap, takeUntil } from 'rxjs';
import { ImgService } from '../../../shared/services/img/img.service';
import {
  changeUploadedImageRotate,
  changeUploadedImageRotateFailed,
  changeUploadedImageRotateSuccess,
  deleteUploadedFilesData,
  initUploadFile,
  initUploadFileFailed,
  initUploadFileSuccess,
  updateFileLocalUrl,
  uploadFileFailed,
  uploadFileSuccess,
} from '../actions/uploading-files.actions';
import { Store } from '@ngrx/store';
import { selectUploadingFilesState } from '../selectors/uploading-files.selectors';
import { UtilsService } from '../../../shared/services/utils.service';
import { errorNotificationMessages } from '../../../shared/constants';
import { cloneFile } from '../../../shared/utils/cloneFile';
// import monitoring from 'src/utils/monitoring';

@Injectable()
export class UploadingFilesEffects {
  private _actions$ = inject(Actions);
  private _postsService = inject(PostsService);
  private _imageService = inject(ImgService);
  private _postService = inject(PostsService);
  private _store = inject(Store);
  private _utils = inject(UtilsService);

  public initUploadFile$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(initUploadFile),
      mergeMap((action) => {
        // На iOS сразу копируем File в память — до FileReader/preview и до HTTP.
        // Иначе исходный File может «протухнуть» → multipart с Content-Length: 0.
        return from(cloneFile(action.file)).pipe(
          switchMap((stableFile) => {
            if (action.withoutLocalUrl) {
              return of(
                initUploadFileSuccess({
                  localId: action.localId,
                  localUrl: '',
                  localName: stableFile.name,
                  localSize: stableFile.size,
                  fileType: action.fileType,
                  file: stableFile,
                  rotateAngle: action.rotateAngle,
                  imageCategory: action.imageCategory,
                  imageProcessingProfile: action.imageProcessingProfile,
                }),
              );
            }

            return this._imageService.imageFileReader(stableFile).pipe(
              concatLatestFrom(() => this._store.select(selectUploadingFilesState)),
              map(
                ([
                  localUrl,
                  {
                    postEditMode,
                    uploadingFiles,
                    uploadingFilesOfEditedPost,
                    uploadingFilesByCategory,
                  },
                ]) => {
                  // Проверяем добавлен ли уже файл с таким localId.
                  // Нужно для конвертации heic и jpeg для отображения превью фото.
                  let uploadedFile;
                  if (action.imageCategory) {
                    uploadedFile = uploadingFilesByCategory?.[action.imageCategory]?.find(
                      (f) => f.localId === action.localId,
                    );
                  } else if (postEditMode) {
                    uploadedFile = uploadingFilesOfEditedPost.find(
                      (f) => f.localId === action.localId,
                    );
                  } else {
                    uploadedFile = uploadingFiles.find((f) => f.localId === action.localId);
                  }

                  if (uploadedFile) {
                    return updateFileLocalUrl({
                      localId: action.localId,
                      localUrl: localUrl as string,
                      imageCategory: action.imageCategory,
                    });
                  }

                  return initUploadFileSuccess({
                    localId: action.localId,
                    localUrl: localUrl as string,
                    localName: stableFile.name,
                    localSize: stableFile.size,
                    fileType: action.fileType,
                    file: stableFile,
                    rotateAngle: action.rotateAngle,
                    imageCategory: action.imageCategory,
                    imageProcessingProfile: action.imageProcessingProfile,
                  });
                },
              ),
            );
          }),
          catchError((error) => {
            const message = error instanceof Error ? error.message : String(error);
            // monitoring.sendMessage(new Error(`File upload init failed: ${message}`), {
            //   fileName: action.file?.name,
            //   fileSize: action.file?.size,
            //   fileType: action.file?.type,
            //   userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
            // });
            return of(initUploadFileFailed({ error: message }));
          }),
        );
      }),
    );
  });

  public uploadFile$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(initUploadFileSuccess),
      mergeMap(
        (action) =>
          this._postsService
            .uploadFile({
              file: action.file,
              rotateAngle: action.rotateAngle,
              imageCategory: action.imageCategory,
              imageProcessingProfile: action.imageProcessingProfile,
            })
            .pipe(
              takeUntil(
                this._actions$.pipe(
                  ofType(deleteUploadedFilesData),
                  filter((deleteAction) => deleteAction.localIdToBeDeleted === action.localId),
                ),
              ),
              switchMap((file) =>
                of(
                  uploadFileSuccess({
                    localId: action.localId,
                    id: file.data.id,
                    imageCategory: action.imageCategory,
                    uploadedFileData: file.data,
                  }),
                ),
              ),
              catchError((error: HttpErrorResponse | unknown) => {
                const message = this._getUploadErrorMessage(error);
                this._utils.openNotification(message, true);
                return of(
                  uploadFileFailed({
                    error: message,
                    localId: action.localId,
                    imageCategory: action.imageCategory,
                  }),
                );
              }),
            ),
        3,
      ),
    );
  });

  public changeUploadedImageRotate$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(changeUploadedImageRotate),
      mergeMap((action) => {
        return this._postService.changeImageRotateAngle(action.id, action.rotateAngle).pipe(
          switchMap(() =>
            of(
              changeUploadedImageRotateSuccess({
                localId: action.localId,
                id: action.id,
                imageCategory: action.imageCategory,
                rotateAngle: action.rotateAngle,
              }),
            ),
          ),
          catchError((error) =>
            of(changeUploadedImageRotateFailed({ localId: action.localId, error: error })),
          ),
        );
      }),
    );
  });

  private _getUploadErrorMessage(error: HttpErrorResponse | unknown): string {
    if (!(error instanceof HttpErrorResponse)) {
      return errorNotificationMessages.default;
    }

    const responseError = error.error?.error;
    const errorMessage =
      typeof responseError?.message === 'string' ? responseError.message.trim() : '';
    const message = typeof error.error?.message === 'string' ? error.error.message.trim() : '';

    return errorMessage || message || errorNotificationMessages.default;
  }
}
